import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useModal } from '../../hooks/useModal';
import AuthHeader from './components/AuthHeader';
import AuthTabs from './components/AuthTabs';
import SocialLoginButtons from './components/SocialLoginButtons';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import RoleSelector from './components/RoleSelector';
import InfoModal from '../../components/ui/InfoModal';

const LoginRegister = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { infoModal, showInfo, closeInfo } = useModal();
  
  // Extract parameters from URL
  const urlRole = searchParams.get('role');
  const urlEmail = searchParams.get('email');
  const urlTab = searchParams.get('tab');
  
  const [activeTab, setActiveTab] = useState(() => {
    // If role is specified (admin/moderator invitation), default to register tab
    if (urlRole && (urlRole === 'admin' || urlRole === 'moderator')) {
      return 'register';
    }
    return urlTab || 'login';
  });
  
  const [selectedRole, setSelectedRole] = useState(() => {
    // If role is specified in URL, use it, otherwise default to 'va'
    return urlRole || 'va';
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Update URL when tab changes (but not during registration process)
  useEffect(() => {
    // Don't update URL if we're in the middle of a loading process
    if (isLoading) return;
    
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', activeTab);
    navigate(`/login-register?${newSearchParams.toString()}`, { replace: true });
  }, [activeTab, navigate, searchParams, isLoading]);

  const handleSocialLogin = (provider) => {
    setIsLoading(true);
    // Simulate social login - implement actual OAuth flow here
    setTimeout(() => {
      showInfo({
        title: 'Feature Coming Soon',
        message: `${provider} login would be implemented here`,
        type: 'info'
      });
      setIsLoading(false);
    }, 1000);
  };

  const handleTabChange = (tab) => {
    if (!isLoading) {
      setActiveTab(tab);
      setShowForgotPassword(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <AuthHeader />

      {/* Main Content */}
      <main>
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-md">
            {/* Welcome Section */}
            <div className="text-center mb-8">
              <img src="/assets/images/Logo/webp/VAS-LOGO-horizontal.webp" alt="VA Sprouts Logo" className="h-36 mx-auto w-auto" />
              <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
                {showForgotPassword 
                  ? 'Reset Your Password'
                    : urlRole === 'admin'
                      ? 'Join as Administrator'
                      : urlRole === 'moderator'
                        ? 'Join as Moderator'
                        : 'Join VA Sprouts'}
              </h1>
              <p className="text-text-secondary">
                {showForgotPassword
                  ? 'Enter your email address and we\'ll send you a link to reset your password'
                  : activeTab === 'login' 
                    ? 'Sign in to access your account and continue your journey'
                    : urlRole === 'admin'
                      ? 'Complete your administrator account setup to manage the platform'
                      : urlRole === 'moderator'
                        ? 'Complete your moderator account setup to help maintain our community'
                        : 'Create your account and start connecting with opportunities'
                }
              </p>
              {(urlRole === 'admin' || urlRole === 'moderator') && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    🎉 You've been invited to join as a {urlRole}! Complete the form below to accept your invitation.
                  </p>
                </div>
              )}
            </div>

            {/* Auth Container */}
            <div className="bg-surface rounded-xl shadow-lg border border-border p-6 sm:p-8">
              {!showForgotPassword && (
                <>
                  {/* Tab Navigation */}
                  <AuthTabs activeTab={activeTab} onTabChange={handleTabChange} />

                  {/* Role Selection for Registration */}
                  {activeTab === 'register' && (
                    <div className="mb-6">
                      <RoleSelector
                        selectedRole={selectedRole}
                        onRoleChange={setSelectedRole}
                        disabled={isLoading || !!urlRole} // Disable if role comes from URL (invitation)
                      />
                      {urlRole && (
                        <p className="mt-2 text-sm text-text-secondary">
                          Role selection is locked because you're registering through an invitation.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Social Login Buttons 
                  <SocialLoginButtons
                    onGoogleLogin={() => handleSocialLogin('Google')}
                    onLinkedInLogin={() => handleSocialLogin('LinkedIn')}
                    isLoading={isLoading}
                  />*/}
                </>
              )}

              {/* Forms */}
              {showForgotPassword ? (
                <ForgotPasswordForm
                  onBackToLogin={handleBackToLogin}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                />
              ) : activeTab === 'login' ? (
                <LoginForm
                  onSocialLogin={handleSocialLogin}
                  onForgotPassword={handleForgotPassword}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                />
              ) : (
                <RegisterForm
                  selectedRole={selectedRole}
                  onSocialLogin={handleSocialLogin}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                />
              )}

              {/* Footer Links */}
              {!showForgotPassword && (
                <div className="mt-6 text-center">
                  <p className="text-sm text-text-secondary">
                    {activeTab === 'login' ? "Don't have an account? " : "Already have an account? "}
                    <button
                      onClick={() => handleTabChange(activeTab === 'login' ? 'register' : 'login')}
                      className="text-primary hover:text-primary-600 font-medium transition-colors duration-200"
                      disabled={isLoading}
                    >
                      {activeTab === 'login' ? 'Create one here' : 'Sign in here'}
                    </button>
                  </p>
                </div>
              )}
            </div>

            {/* Additional Info */}
            <div className="mt-8 text-center">
              <p className="text-xs text-text-muted">
                By continuing, you agree to our Terms of Service and Privacy Policy.
                <br />
                Secure authentication powered by industry-standard encryption.
              </p>
              
              {/* Marketing Links */}
              {/* <div className="mt-6 flex flex-wrap justify-center gap-4">
                <a 
                  href="/home" 
                  className="text-sm text-primary hover:text-primary-600 font-medium transition-colors duration-200"
                >
                  Homepage
                </a>
                <a 
                  href="/how-it-works" 
                  className="text-sm text-primary hover:text-primary-600 font-medium transition-colors duration-200"
                >
                  How It Works
                </a>
                <a 
                  href="/skills-directory" 
                  className="text-sm text-primary hover:text-primary-600 font-medium transition-colors duration-200"
                >
                  Skills Directory
                </a>
              </div> */}
            </div>
          </div>
        </div>
      </main>

      {/* Info Modal */}
      {infoModal && (
        <InfoModal
          title={infoModal.title}
          message={infoModal.message}
          type={infoModal.type}
          buttonLabel={infoModal.buttonLabel}
          onClose={closeInfo}
        />
      )}
    </div>
  );
};

// Forgot Password Form Component
const ForgotPasswordForm = ({ onBackToLogin, isLoading, setIsLoading }) => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email.trim())) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Import authService dynamically to avoid circular dependencies
      const { default: authService } = await import('../../utils/authService');
      const result = await authService.resetPassword(email.trim());

      if (result.success) {
        setSuccess(true);
      } else {
        setErrors({ submit: result.error || 'Failed to send reset email' });
      }
    } catch (error) {
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-text-primary mb-2">Email Sent!</h3>
        <p className="text-text-secondary mb-6">
          We've sent a password reset link to <strong>{email}</strong>. 
          Please check your email and follow the instructions to reset your password.
        </p>
        <button
          onClick={onBackToLogin}
          className="text-primary hover:text-primary-600 font-medium transition-colors duration-200"
        >
          ← Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="reset-email" className="block text-sm font-medium text-text-primary mb-2">
          Email Address
        </label>
        <input
          id="reset-email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
          }}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
            errors.email ? 'border-red-500' : 'border-border'
          }`}
          placeholder="Enter your email address"
          disabled={isLoading}
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-600">{errors.email}</p>
        )}
      </div>

      {errors.submit && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{errors.submit}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? 'Sending...' : 'Send Reset Link'}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={onBackToLogin}
          className="text-text-secondary hover:text-text-primary transition-colors duration-200"
          disabled={isLoading}
        >
          ← Back to Sign In
        </button>
      </div>
    </form>
  );
};

export default LoginRegister;
